import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import shared from '../sharedStyles';
import useIsMobile from '../hooks/useIsMobile';
import PageMeta from '../components/PageMeta';
import useAppConfig from '../hooks/useAppConfig';
import { INCLUDED } from '../marketing/pricing';
import ProgramTypePicker from '../components/ProgramTypePicker';
import YmcaPicker from '../components/YmcaPicker';
import SeasonField from '../components/SeasonField';
import { isFreeProgram, programTypeLabel, programTypeFromQuery } from '../utils/programType';

import HelpTip from '../components/HelpTip';
const CoachRegister = () => {
  const navigate = useNavigate();
  const { loginCoach } = useAuth();
  const isMobile = useIsMobile();
  const styles = isMobile ? mobileStyles(baseStyles) : baseStyles;
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    coachName: '',
    coachEmail: '',
    coachPhone: '',
    coachTitle: 'Head Coach',
    password: '',
    confirmPassword: '',
    programName: '',
    shortName: '',
    season: '',
    location: '',
    ageGroup: 'U15 (13-15 yrs)',
    monthlyFee: 150,
    bio: '',
    programType: programTypeFromQuery(searchParams.get('type')),
    ymcaId: '',
    ymcaOther: '',
  });
  const { features, price } = useAppConfig();
  const typesOn = features.programTypes;

  const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleNext = (e) => {
    e.preventDefault();
    setError('');
    if (step === 1) {
      if (form.password !== form.confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      if (form.password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
    }
    if (step === 2 && typesOn) {
      if (!form.programType) {
        setError('Choose how your program works: Paid, Free, or Free — YMCA.');
        return;
      }
      if (form.programType === 'paid' && !(Number(form.monthlyFee) > 0)) {
        setError('Enter the monthly fee per player for a paid program.');
        return;
      }
      if (form.programType === 'free_ymca' && !form.ymcaId && !form.ymcaOther.trim()) {
        setError('Select your YMCA or enter its name.');
        return;
      }
    }
    setStep(s => s + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Behind the flag the wizard never shows the picker; send v1 shape.
      const payload = typesOn
        ? { ...form, monthlyFee: isFreeProgram(form.programType) ? 0 : form.monthlyFee }
        : { ...form, programType: undefined, ymcaId: undefined, ymcaOther: undefined };
      const res = await authAPI.coachRegister(payload);
      const { token, coach } = res.data;
      loginCoach(token, coach);
      navigate('/coach/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Your Account', 'Your Program', 'Review'];

  return (
    <div style={styles.container}>
      <PageMeta
        title="Start Your 14-Day Free Trial | HoopHero"
        description="Set up your youth basketball program in minutes. No credit card required for the first 14 days. Cancel anytime."
        path="/coach/register"
      />
      <div style={styles.box}>
        <img src="/logo-wide.png" alt="HoopHero" style={styles.logoImg} />
        <h1 style={{ margin: 0, fontWeight: 400, ...styles.tagline }}>Set up your basketball program</h1>

        <div style={styles.stepBar}>
          {steps.map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: step > i + 1 ? '#14855A' : step === i + 1 ? '#C2460B' : '#ECEEF3',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '13px', fontWeight: '700',
                color: step >= i + 1 ? '#fff' : '#9AA0B0',
                flexShrink: 0,
              }}>
                {step > i + 1 ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: '13px', color: step === i + 1 ? '#0D1117' : '#9AA0B0', fontWeight: step === i + 1 ? '600' : '400' }}>
                {s}
              </span>
              {i < steps.length - 1 && <div style={{ width: '20px', height: '1px', background: '#ECEEF3', marginLeft: '4px' }} />}
            </div>
          ))}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {step === 1 && (
          <form onSubmit={handleNext} style={styles.form}>
            <div style={styles.sectionLabel}>Your Info</div>
            <div style={styles.grid2}>
              <div style={styles.field}>
                <label style={shared.label}>Your Name</label>
                <input style={shared.input} placeholder="Alex Smith" value={form.coachName} onChange={e => update('coachName', e.target.value)} required />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Title</label>
                <input style={shared.input} placeholder="Head Coach" value={form.coachTitle} onChange={e => update('coachTitle', e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Email Address</label>
                <input style={shared.input} type="email" placeholder="coach@email.com" value={form.coachEmail} onChange={e => update('coachEmail', e.target.value)} required />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Phone Number</label>
                <input style={shared.input} placeholder="(904) 555-0100" value={form.coachPhone} onChange={e => update('coachPhone', e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Password</label>
                <input style={shared.input} type="password" placeholder="Min 8 characters" value={form.password} onChange={e => update('password', e.target.value)} required />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Confirm Password</label>
                <input style={shared.input} type="password" placeholder="Confirm password" value={form.confirmPassword} onChange={e => update('confirmPassword', e.target.value)} required />
              </div>
            </div>
            <button type="submit" style={styles.btnOrange}>Next — Program Info →</button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleNext} style={styles.form}>
            <div style={styles.sectionLabel}>Your Program</div>
            <div style={styles.grid2}>
              <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                <label style={shared.label}>Program / Team Name</label>
                <input style={shared.input} placeholder="Jacksonville Jaguars Youth Basketball" value={form.programName} onChange={e => update('programName', e.target.value)} required />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Short Name</label>
                <input style={shared.input} placeholder="Jaguars" value={form.shortName} onChange={e => update('shortName', e.target.value)} />
              </div>
              <div style={styles.field}>
                <SeasonField id="season" value={form.season} onChange={(v) => update('season', v)} />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>City / Location</label>
                <input style={shared.input} placeholder="Jacksonville, FL" value={form.location} onChange={e => update('location', e.target.value)} />
              </div>
              <div style={styles.field}>
                <label style={shared.label}>Age Group</label>
                <select style={shared.input} value={form.ageGroup} onChange={e => update('ageGroup', e.target.value)}>
                  <option>U10 (8-10 yrs)</option>
                  <option>U12 (10-12 yrs)</option>
                  <option>U13 (11-13 yrs)</option>
                  <option>U15 (13-15 yrs)</option>
                  <option>U17 (15-17 yrs)</option>
                  <option>Varsity</option>
                  <option>Adult League</option>
                </select>
              </div>
              {typesOn && (
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <label style={{ ...shared.label, display: 'inline-block' }}>How does your program work?</label><HelpTip id="register.programType" />
                  <ProgramTypePicker value={form.programType} onChange={(v) => { update('programType', v); setError(''); }} />
                </div>
              )}
              {(!typesOn || form.programType === 'paid') && (
                <div style={styles.field}>
                  <label style={shared.label}>Monthly Fee per Player ($)</label>
                  <input style={shared.input} type="number" min="0" value={form.monthlyFee} onChange={e => update('monthlyFee', e.target.value)} />
                </div>
              )}
              {typesOn && form.programType === 'free_ymca' && (
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                  <YmcaPicker
                    value={{ ymcaId: form.ymcaId, ymcaOther: form.ymcaOther, label: form.ymcaLabel || '', state: form.ymcaState || '' }}
                    onChange={({ ymcaId, ymcaOther, label, state }) => setForm(f => ({ ...f, ymcaId, ymcaOther, ymcaLabel: label, ymcaState: state }))}
                  />
                </div>
              )}
              <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                <label style={shared.label}>Bio / About You (optional)</label>
                <textarea style={{ ...shared.input, minHeight: '70px', resize: 'vertical' }} placeholder="Brief coaching background..." value={form.bio} onChange={e => update('bio', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" style={styles.btnGray} onClick={() => setStep(1)}>Back</button>
              <button type="submit" style={styles.btnOrange}>Next — Review →</button>
            </div>
          </form>
        )}

        {step === 3 && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.sectionLabel}>Review Your Details</div>
            <div style={styles.reviewCard}>
              <div style={styles.reviewSection}>Coach</div>
              {[
                ['Name', form.coachName],
                ['Email', form.coachEmail],
                ['Phone', form.coachPhone || 'Not provided'],
                ['Title', form.coachTitle],
              ].map(([l, v]) => (
                <div key={l} style={styles.reviewRow}>
                  <span style={styles.reviewLabel}>{l}</span>
                  <span style={styles.reviewValue}>{v}</span>
                </div>
              ))}
            </div>
            <div style={styles.reviewCard}>
              <div style={styles.reviewSection}>Program</div>
              {[
                ['Program Name', form.programName],
                ['Short Name', form.shortName || form.programName],
                ['Season', form.season],
                ['Location', form.location || 'Not provided'],
                ['Age Group', form.ageGroup],
                ...(typesOn ? [['Program Type', programTypeLabel(form.programType)]] : []),
                ...(typesOn && form.programType === 'free_ymca' ? [['YMCA', form.ymcaLabel || form.ymcaOther]] : []),
                ...(!typesOn || form.programType === 'paid' ? [['Monthly Fee', '$' + form.monthlyFee + '/player']] : []),
              ].map(([l, v]) => (
                <div key={l} style={styles.reviewRow}>
                  <span style={styles.reviewLabel}>{l}</span>
                  <span style={styles.reviewValue}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ background: '#E4F5ED', borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: '#14855A' }}>
              {`Your program will get a unique join link to share with parents. 14-day free trial, then ${price} for ${INCLUDED}. Cancel anytime.`}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" style={styles.btnGray} onClick={() => setStep(2)}>Back</button>
              <button type="submit" style={styles.btnOrange} disabled={loading}>
                {loading ? 'Creating your basketball program...' : 'Create My Basketball Program'}
              </button>
            </div>
            <div style={{ fontSize: '11.5px', color: '#9AA0B0', textAlign: 'center', marginTop: '14px', lineHeight: '1.5' }}>
              By creating an account, you agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#C2460B', textDecoration: 'underline' }}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#C2460B', textDecoration: 'underline' }}>Privacy Policy</a>.
            </div>
          </form>
        )}

        <div style={styles.footer}>
          Already have an account? <button style={styles.link} onClick={() => navigate('/coach/login')}>Sign in</button>
        </div>
      </div>
    </div>
  );
};

const baseStyles = {
  container: { minHeight: '100vh', background: '#F6F7FA', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  // boxSizing:'border-box' is critical: width:100% + 80px horizontal padding
  // would otherwise overflow the viewport on small screens, pushing the
  // entire card (and every input inside it) past the right edge.
  box: { background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '36px 40px', width: '100%', maxWidth: '560px', boxSizing: 'border-box' },
  logo: { fontFamily: 'Georgia, serif', fontSize: '28px', fontWeight: '900', textAlign: 'center', marginBottom: '4px', color: '#0D1117' },
  logoImg: { display: 'block', width: '300px', maxWidth: '100%', height: 'auto', margin: '0 auto 10px' },
  accent: { color: '#C2460B' },
  tagline: { textAlign: 'center', fontSize: '14px', color: '#5A6070', marginBottom: '24px' },
  stepBar: { display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '24px', flexWrap: 'wrap' },
  error: { background: '#FDEAEA', color: '#C93535', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px' },
  form: { display: 'flex', flexDirection: 'column', gap: '14px' },
  sectionLabel: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#9AA0B0' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px' },
  btnOrange: { background: '#C2460B', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', flex: 1 },
  btnGray: { background: '#ECEEF3', color: '#0D1117', border: 'none', borderRadius: '8px', padding: '11px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  reviewCard: { border: '1px solid rgba(0,0,0,0.08)', borderRadius: '8px', padding: '14px' },
  reviewSection: { fontSize: '11px', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#9AA0B0', marginBottom: '10px' },
  reviewRow: { display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid rgba(0,0,0,0.04)', fontSize: '13px' },
  reviewLabel: { color: '#5A6070' },
  reviewValue: { fontWeight: '600', color: '#0D1117' },
  footer: { textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#5A6070' },
  link: { background: 'none', border: 'none', color: '#C2460B', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
};

// Mobile overrides — compressed aggressively so step 1 (Your Info, the
// 6-field form) fits on a single mobile viewport without scrolling. Every
// number here was tuned to claw back vertical space:
//   · logo 300 → 225 px (saves ~26px of height + makes the form feel less
//     marketing-heavy now that the user is committing to sign up)
//   · tagline / stepBar / form / footer margins all tightened
//   · grid2 collapses to 1 column, fields stack
//   · input padding shrinks via shared.input mobile branch (separate file)
const mobileStyles = (s) => ({
  ...s,
  container: { ...s.container, padding: '10px' },
  box: { ...s.box, padding: '16px 16px' },
  logo: { ...s.logo, fontSize: '24px' },
  logoImg: { ...s.logoImg, width: '225px', margin: '0 auto 6px' },
  tagline: { ...s.tagline, fontSize: '12.5px', marginBottom: '12px' },
  stepBar: { ...s.stepBar, marginBottom: '14px', gap: '3px' },
  form: { ...s.form, gap: '10px' },
  sectionLabel: { ...s.sectionLabel, fontSize: '10px' },
  // Stack the two-column form fields vertically on mobile so labels/inputs
  // are full width and easy to fill in.
  grid2: { ...s.grid2, gridTemplateColumns: '1fr', gap: '9px' },
  field: { ...s.field, gap: '3px' },
  footer: { ...s.footer, marginTop: '12px', fontSize: '12.5px' },
});

export default CoachRegister;